// For the Fast Brain — brain states (order follows the Brain Day Compass) and their worksheets.
import { applyLayout } from './fastBrainLayouts';
// Field types: text | lines (optional star to "circle") | checklist (tick + write) | options (fixed ticks) | groups (several titled lists)
const IMG = '/images/fast-brain';

export const compass = {
  id: 'compass',
  title: "Where's Your Brain Today?",
  eyebrow: 'The Brain Day Compass',
  subtitle: 'Find your starting point. Not another thing to figure out.',
  image: `${IMG}/compass.jpg`,
};

export const brainStates = [
  { id: 'full', name: 'Full', hint: 'Too many thoughts', color: '#6b3a5e', bg: '#efe6ee' },
  { id: 'stuck', name: 'Stuck', hint: "Can't begin", color: '#1f5068', bg: '#e3eef2' },
  { id: 'scattered', name: 'Scattered', hint: 'Everything at once', color: '#3f5e2f', bg: '#e8eee3' },
  { id: 'lost', name: 'Lost', hint: 'Where did time go?', color: '#a2521d', bg: '#f6ebe0' },
  { id: 'sparking', name: 'Sparking', hint: 'Too many ideas', color: '#a8740f', bg: '#f7efd9' },
];

const nextStep = (hint) => ({ type: 'text', id: 'next', label: 'My next tiny step', hint });

const rawSheets = [
  {
    id: 'brain-unload',
    state: 'full',
    title: 'The Great Brain Unload',
    tagline: 'Get it out of your head',
    image: `${IMG}/brain-unload.jpg`,
    when: "Your mind is full, busy, and running in every direction. It's time to empty the mental tabs.",
    how: ['Set a timer for 5–10 minutes.', 'Write down everything on your mind.', 'No organizing, fixing or deciding yet — just get it all out.'],
    fields: [
      { type: 'lines', id: 'mind', label: 'On My Mind Today…', rows: 14, star: true, starHint: 'Tap the star to "circle" the 1–3 things that matter most today.' },
      nextStep('Look at your list and gently circle 1–3 things that feel most important or need attention today.'),
    ],
  },
  {
    id: 'fishbone',
    state: 'full',
    title: 'The Fishbone Method',
    tagline: 'Sort • Clarify • See the bigger picture',
    image: `${IMG}/fishbone.jpg`,
    when: 'Your mind is overflowing with lots of thoughts, ideas, tasks and "randoms" and it all feels like one big tangle.',
    how: ['Choose a main focus or question.', 'Add 4–6 key categories (the "bones").', 'Sort your thoughts from your brain dump into each category.'],
    fields: [
      { type: 'text', id: 'focus', label: 'Main focus or question' },
      { type: 'groups', id: 'bones', count: 6, rows: 5, titlePlaceholder: 'Category', editableTitles: true },
      nextStep('Look at your fishbone and circle one category or idea to take action on today.'),
    ],
  },
  {
    id: 'thought-sorter',
    state: 'full',
    title: 'The Thought Sorter',
    tagline: 'Decide what deserves your attention',
    image: `${IMG}/thought-sorter.jpg`,
    when: "You've got everything out of your head and now you need to make sense of it all and decide what's next.",
    how: ['Look at your list(s) or fishbone.', 'Sort each thought, task or idea into one of the four boxes below.', 'Be honest, simple and kind to yourself.'],
    fields: [
      {
        type: 'groups', id: 'sort', rows: 5, checkbox: true,
        titles: [
          { title: 'Do', sub: 'Needs my attention now.' },
          { title: 'Plan', sub: 'Important, but not today.' },
          { title: 'Park', sub: 'Ideas for another day.' },
          { title: 'Release', sub: 'Not mine, not necessary, or not worth my energy.' },
        ],
      },
      nextStep("What's ONE thing I can do in the next 10 minutes?"),
    ],
  },
  {
    id: 'starting-ladder',
    state: 'stuck',
    title: 'The Starting Ladder',
    tagline: 'Turn overwhelm into a doable first step',
    image: `${IMG}/starting-ladder.jpg`,
    when: "You know what needs to be done, but you just can't seem to start. It feels heavy, overwhelming or like too much.",
    how: ["Write the thing you're stuck on.", 'Break it down into smaller steps (on the ladder).', 'Start at the bottom with the easiest step — build from there.'],
    fields: [
      { type: 'text', id: 'stuck', label: "The thing I'm stuck on" },
      { type: 'checklist', id: 'ladder', label: 'The ladder', labels: ['Step 5', 'Step 4', 'Step 3', 'Step 2', 'Step 1'], hint: 'Start at the bottom — Step 1 is the easiest.' },
      {
        type: 'options', id: 'easier', label: 'Make it easier', hint: 'If it still feels hard, try one (or more) of these:',
        options: ['Make it smaller (tiny step).', 'Set a 5–10 minute timer.', 'Remove a distraction.', 'Get my space ready (clear, simple, inviting).', 'Add a body reset (breathe, move, stretch).', 'Ask for support or a little accountability.', 'Make it more fun or rewarding.'],
      },
      nextStep("The one action I'll take right now (or in the next 10 minutes) is:"),
    ],
  },
  {
    id: 'blocking-me',
    state: 'stuck',
    title: "What's Actually Blocking Me?",
    tagline: 'A quick check-in to find the real roadblock',
    image: `${IMG}/blocking-me.jpg`,
    when: "When you want to get started but keep hitting a wall. This helps you identify what's really going on so you can choose a useful next step.",
    how: ['Check the box(es) that feel true right now.', 'See the matching support idea.', 'Choose one small action to try.'],
    fields: [
      {
        type: 'pairs', id: 'blocks', label: 'What might be going on?', hint: 'Tick what feels true — the helpful first step lights up beside it.',
        items: [
          { title: 'Overwhelmed', sub: 'It feels like too much.', help: ['Shrink it down.', 'Choose just one tiny piece (2 minutes or less).'] },
          { title: 'Unclear', sub: "I'm not sure where to start.", help: ['Get clear.', 'Define the next small action (e.g., open the file, make a list, gather info).'] },
          { title: 'Low energy', sub: "I'm tired, drained or in a slump.", help: ['Support your system.', 'Hydrate, move your body, get some fresh air or take a 5-minute reset.'] },
          { title: 'Anxious / Afraid', sub: "I'm worried it won't be good enough (or I'll mess it up).", help: ['Lower the stakes.', 'Give yourself permission to do a "rough draft" or "good enough" version.'] },
          { title: 'Perfectionism', sub: 'I want to do it the right way.', help: ['Aim for progress, not perfect.', 'Set a time limit or a simple version (e.g., 10-minute draft).'] },
          { title: 'Not interested', sub: "It just doesn't feel important (right now).", help: ['Check in.', 'Is it truly not a priority, or just bad timing? Adjust, delegate, or let it go.'] },
          { title: 'Emotionally heavy', sub: "I'm feeling sad, stressed, or unsettled.", help: ['Acknowledge it.', 'Take a few minutes to feel, breathe, journal or do something that soothes you.'] },
          { title: 'Distracted', sub: 'My attention keeps going everywhere else.', help: ['Reduce the noise.', 'Turn off notifications, clear your space, or use a focus timer (e.g., 10 minutes).'] },
          { title: 'Something else?', sub: "There's another reason…" },
        ],
      },
      nextStep('Based on what I checked, the one small action I’ll take now is:'),
    ],
  },
  {
    id: 'two-minute-launch',
    state: 'stuck',
    title: 'The Two-Minute Launch',
    tagline: 'A ridiculously simple way to get moving',
    image: `${IMG}/two-minute-launch.jpg`,
    when: "When you know what you need to do, but can't seem to get started. This helps you break through the resistance and build momentum.",
    how: ['Choose one task.', 'Break it down into a two-minute starter (or less).', 'Do just two minutes — then decide: keep going or stop (both are a win).'],
    fields: [
      { type: 'lines', id: 'task', label: 'The task', num: 1, tone: 'green', center: true, icon: 'Target', hint: 'What do I want to get done?', rows: 4 },
      { type: 'lines', id: 'starter', label: 'The 2-minute starter', num: 2, tone: 'blue', center: true, icon: 'Hourglass', hint: "What's the tiniest action I can do in two minutes (or less)?", rows: 4 },
      { type: 'options', id: 'result', single: true, label: 'The result', num: 3, tone: 'orange', center: true, icon: 'CheckCircle2', hint: 'After two minutes, I will:', options: ["Keep going (I've got momentum!)", "Stop for now (and that's still progress!)"] },
      {
        type: 'options', id: 'help', label: 'Need a little help?', tone: 'white', inner: false, hint: 'Try one of these two-minute starters:',
        options: ['Open the document or app.', 'Clear a small space (desk, counter, bag).', 'Do the first step only.', 'Set a timer for 2 minutes and just begin.', 'Gather what I need (tools, info, supplies).', 'Write a quick outline or rough version.'],
      },
      { type: 'quote', id: 'remember', label: 'Remember…', icon: 'Lightbulb', tone: 'white', bullets: true, lines: ['The goal is to START, not finish.', 'Two minutes is easy, low pressure and usually enough to shift your energy.', 'Momentum often shows up after you begin.', "Even if you stop, you've still made progress!"] },
      nextStep("The two-minute action I'll do right now is:"),
    ],
    layout: [
      { cols: ['task', 'starter', 'result'] },
      { cols: ['help', 'remember'], joined: true },
      { cols: ['next'] },
    ],
  },
  {
    id: 'parking-lot',
    state: 'scattered',
    title: 'The Distraction Parking Lot',
    tagline: 'Park it for now so you can stay focused',
    image: `${IMG}/parking-lot.jpg`,
    when: 'When your attention keeps getting pulled in different directions and you need a place to park those thoughts so you can focus.',
    how: ['When a new thought, reminder or idea pops up, write it in the parking lot (instead of chasing it).', 'Keep it out of your head and on the page.', "Return to it later when it's the right time."],
    fields: [
      { type: 'lines', id: 'focus', label: 'My focus today', hint: "The main thing I'm keeping my attention on:", rows: 2, boxed: true },
      { type: 'options', id: 'window', single: true, inline: true, other: true, label: 'My focus window', hint: "I'm going to focus for:", options: ['5 min', '10 min', '15 min', '25 min'], boxed: true },
      { type: 'checklist', id: 'lot', label: 'My distraction parking lot', hint: 'Park each pop-up thought here — tick it off when you come back to it.', labels: Array(10).fill(''), boxed: true },
      {
        type: 'options', id: 'checkin', label: 'My check-in', hint: "When my focus window is done, I'll:", boxed: true,
        options: ['Review my parking lot.', "Choose what's next.", 'Move one item to my Now / Next / Later list.', "Let go of what's not needed right now."],
      },
      { type: 'quote', id: 'quote', lines: ['Capture it, park it, and come back to it —', 'with a clearer, calmer brain.'] },
      nextStep("The one thing I'll do right now is:"),
    ],
  },
  {
    id: 'one-thing-bubble',
    state: 'scattered',
    title: 'The One-Thing Bubble',
    tagline: 'Give your brain one thing to focus on',
    image: `${IMG}/one-thing-bubble.jpg`,
    when: "When your mind is jumping around, you feel scattered, or you keep getting distracted and can't seem to stay on one thing.",
    how: ['Choose ONE thing to focus on right now.', 'Park the other thoughts around the bubble.', 'Set a focus time, get started, and return if you get pulled away.'],
    fields: [
      { type: 'lines', id: 'one', label: 'My one thing right now', rows: 3, boxed: true },
      {
        type: 'groups', id: 'around', rows: 6, checkbox: true,
        titles: [
          { title: 'My brain distractions', sub: 'Thoughts, reminders, ideas and temptations — park them here for now.' },
          { title: 'Other things popping up', sub: 'More thoughts, tabs, "shoulds," or rabbit holes — they can wait.' },
        ],
      },
      { type: 'options', id: 'window', single: true, inline: true, other: true, label: 'My focus window', hint: "I'm going to focus for:", options: ['5 min', '10 min', '15 min', '25 min'], boxed: true },
      { type: 'lines', id: 'return', label: 'My return point', hint: "If I get distracted, I'll come back to:", rows: 2, boxed: true },
      { type: 'quote', id: 'quote', lines: ['You can have 47 thoughts.', "You don't have to follow all 47."] },
      nextStep("The one thing I'll do right now is:"),
    ],
  },
  {
    id: 'now-next-later',
    state: 'scattered',
    title: 'The Now / Next / Later Plan',
    tagline: 'Bring focus to a busy, bouncy brain',
    image: `${IMG}/now-next-later.jpg`,
    when: 'When your mind is jumping between a million things, and you need a simple way to focus and feel more grounded.',
    how: ['Brain dump your to-dos, ideas and reminders.', 'Sort them into Now, Next or Later.', "Focus on your Now list and let the rest wait (you'll come back to it!)."],
    fields: [
      { type: 'lines', id: 'dump', label: 'Brain dump', hint: "Get it out of your head! Write everything that's on your mind, big or small.", rows: 6, boxed: true },
      {
        type: 'groups', id: 'plan', rows: 6, checkbox: true,
        titles: [
          { title: 'Now', sub: 'Do today. Keep it small and doable.' },
          { title: 'Next', sub: 'Soon, but not today. Plan it in.' },
          { title: 'Later', sub: 'Good ideas, someday, or when I have more capacity.' },
        ],
      },
      { type: 'quote', id: 'tip', label: 'Focus tip', lines: ["You don't have to do it all.", 'Choose ONE thing from your NOW list and give it your full attention (for a set time, if that helps).'] },
      nextStep("The one thing I'll do right now is:"),
    ],
  },
  {
    id: 'reverse-planning',
    state: 'lost',
    title: 'Reverse Planning',
    tagline: 'Start with the end, then work backwards',
    image: `${IMG}/reverse-planning.jpg`,
    when: "When a goal, project or task feels too big, confusing or you're not sure where to start.",
    how: ['Start with your end goal (the finish line).', 'Identify the key steps to get there.', 'Break it down into smaller, doable actions in the right order.'],
    fields: [
      { type: 'lines', id: 'goal', label: 'My end goal', hint: 'What I want to have, do or complete:', rows: 2, boxed: true },
      { type: 'checklist', id: 'steps', label: 'The backward steps', hint: 'List the key steps in reverse order (from finish to start). Step 5 is closest to the goal — Step 1 is where you start.', labels: ['Step 5', 'Step 4', 'Step 3', 'Step 2', 'Step 1'], boxed: true },
      { type: 'checklist', id: 'first', label: 'My first step', hint: "The next small action I'll take:", labels: [''], boxed: true },
      { type: 'options', id: 'when', single: true, inline: true, other: true, label: "I'll do it when…", options: ['Today', 'This week', '10 minutes', '15 minutes'], boxed: true },
      { type: 'text', id: 'by', label: 'Or by (date)', hint: 'Optional — pick a date to finish my first step by.' },
      { type: 'text', id: 'next', label: 'My commitment', hint: "I'm moving forward by completing:" },
    ],
  },
  {
    id: 'time-reality',
    state: 'lost',
    title: 'The Time Reality Check',
    tagline: 'Get a realistic view of your time, energy & capacity',
    image: `${IMG}/time-reality.jpg`,
    when: 'When time feels slippery, you keep running out of it, or everything feels like it takes longer than it should.',
    how: ['List what you need (or want) to get done.', 'Estimate how long it will actually take (be honest!).', "Prioritize and choose what's realistic for right now."],
    fields: [
      { type: 'table', id: 'tasks', rows: 8, label: 'My task list · Reality time · Priority', hint: "What's on my plate, how long will it really take, and what's most important right now? (Must Do = today or soon · Good to Do = helps me move forward · Can Wait = later, delegate, or let go)", priorities: ['Must Do', 'Good to Do', 'Can Wait'], totalLabel: 'Total reality time', boxed: true },
      { type: 'hours', id: 'snapshot', label: 'My time snapshot', hint: 'A quick look at my real life today.', items: ['Sleep', 'Work / Commitments', 'Movement / Self-care', 'Meals / Daily life', 'Other (travel, family, etc.)'], boxed: true },
      { type: 'checklist', id: 'plan', label: 'My realistic plan', hint: "What I'm choosing to focus on today:", labels: ['1', '2', '3'], boxed: true },
      { type: 'quote', id: 'remember', label: 'Remember…', bullets: true, lines: ['Not everything has to be done today.', 'Accurate time estimates reduce stress.', 'Realistic plans build momentum.', 'You can always adjust as you go.'] },
      nextStep("The one thing I'll do right now is:"),
    ],
  },
  {
    id: 'time-blocking',
    state: 'lost',
    title: 'Visual Time Blocking',
    tagline: 'Create a simple, doable plan for your time & energy',
    image: `${IMG}/time-blocking.jpg`,
    when: 'When you feel overwhelmed, unsure where to start, or like your time is getting away from you.',
    how: ['Identify your key priorities (not a long to-do list).', 'Block in time for them — visually.', 'Keep it flexible and realistic (this is a guide, not a rigid schedule).'],
    fields: [
      { type: 'checklist', id: 'top3', label: 'My top 3 priorities', hint: '(for today or this week)', labels: ['1', '2', '3'], boxed: true },
      { type: 'blocks', id: 'blocks', rows: 8, label: 'My time blocks', hint: 'Give your key activities a time "home."', boxed: true },
      {
        type: 'options', id: 'ingredients', label: 'My time ingredients', hint: 'A balanced day — pick what you need and add a realistic time block for each:', boxed: true,
        options: ['Movement / Body', 'Recovery / Reset (breath, meditation, rest)', 'Nourishment (meals, hydration, prep)', 'Focused Work (projects, admin, creating)', 'Connection (people, support, community)', 'Nature / Fresh Air', 'Me Time (something I enjoy)', 'Sleep / Wind Down'],
      },
      { type: 'lines', id: 'flex', label: 'My flex space', hint: 'Life happens. Use this space for the unexpected, or simply to breathe.', rows: 3, boxed: true },
      { type: 'options', id: 'tips', label: 'Tips for success', boxed: true, options: ['Start with less (you can always add).', 'Use visual cues (colours, icons, highlighters).', 'Include short reset breaks (even 5 minutes helps).', 'Keep it realistic for your current energy.'] },
      { type: 'quote', id: 'quote', lines: ['A little structure creates more freedom — not less.'] },
      nextStep("The first time block I'll set up is:"),
    ],
  },
  {
    id: 'inspiration-map',
    state: 'sparking',
    title: 'The Inspiration Action Map',
    tagline: 'Turn your ideas and energy into real momentum',
    image: `${IMG}/inspiration-map.jpg`,
    when: "When you're feeling inspired, energized and motivated, and you want to turn that spark into real, doable action.",
    how: ["Capture what's lighting you up right now.", "Choose one main focus (so you don't spread your energy too thin).", 'Break it into simple steps and take the first one.'],
    fields: [
      { type: 'checklist', id: 'sparks', label: "What's sparking for me right now?", hint: "Things I'm excited about, curious to explore or wanting to create:", labels: Array(5).fill(''), boxed: true },
      { type: 'text', id: 'focus', label: 'My focus idea', hint: "The one thing I'm choosing to move forward with:" },
      { type: 'lines', id: 'why', label: 'My why', hint: 'Why this matters to me (how it will feel, support me, or what it will create):', rows: 2, boxed: true },
      {
        type: 'groups', id: 'plan', rows: 3, checkbox: true,
        titles: [
          { title: 'First steps', sub: '(this week)' },
          { title: 'Next steps', sub: '(next 2–4 weeks)' },
          { title: 'The bigger picture', sub: '(down the road)' },
        ],
      },
      { type: 'checklist', id: 'bumps', label: 'Potential bumps', hint: "What might get in the way, and how I'll support myself:", labels: ['', ''], boxed: true },
      { type: 'checklist', id: 'supports', label: 'My supports', hint: 'What (or who) will help me stay on track?', labels: ['', ''], boxed: true },
      { type: 'quote', id: 'quote', lines: ['Inspiration is the spark — action is what brings it to life.'] },
      nextStep("The one action I'll take in the next 24–48 hours is:"),
    ],
  },
  {
    id: 'energy-reset',
    state: 'sparking',
    title: 'The Energy Reset',
    tagline: 'Refuel your mind, body & motivation',
    image: `${IMG}/energy-reset.jpg`,
    when: 'When you feel low on energy, mentally foggy, unmotivated or just need a quick reset to get your spark back.',
    how: ['Check in on your current energy level.', 'Choose a few simple, doable actions from the menu.', 'Notice the shift, and keep what works!'],
    fields: [
      { type: 'options', id: 'level', single: true, label: 'My energy check-in', hint: 'Right now, my energy feels:', options: ['Drained / Flat', 'Low / Sluggish', 'Okay / Getting by', 'Good / Productive', 'Buzzing / Inspired'], boxed: true },
      {
        type: 'options', id: 'menu', label: 'My quick reset menu', hint: 'Check a few to try right now.', boxed: true,
        options: ['Move my body (5–10 min)', 'Get some fresh air or sunshine', 'Drink water (and maybe minerals)', 'Take 5 slow, deep breaths', 'Stretch or shake it out', 'Do something creative', 'Change my environment (tidy, open a window, music)', 'Nourish my body (real food)', 'Connect with someone (a call, text, or hug)', 'Take a short break (no screen)', 'Do one small win (easy task)'],
      },
      {
        type: 'groups', id: 'energy', rows: 5, checkbox: true,
        titles: [
          { title: 'My energy boosters', sub: 'The things that reliably lift my energy and mood.' },
          { title: 'My energy drains', sub: 'The things that tend to zap my energy (so I can be more mindful of them).' },
        ],
      },
      { type: 'quote', id: 'quote', lines: ['Small shifts can create a big shift', 'in your energy and focus.'] },
      nextStep("The one energizing action I'll take right now is:"),
    ],
  },
  {
    id: 'idea-incubator',
    state: 'sparking',
    title: 'The Idea Incubator',
    tagline: 'Capture, explore and grow your ideas',
    image: `${IMG}/idea-incubator.jpg`,
    when: 'When your mind is buzzing with ideas, you feel inspired, or you want to capture possibilities without losing focus.',
    how: ['Brain dump your ideas (no editing!).', 'Highlight the ones that light you up most.', 'Choose one to explore a little deeper and take a next step.'],
    fields: [
      { type: 'lines', id: 'dump', label: 'My idea dump', hint: 'Get it all out! Big, small, wild or "someday" ideas — they all count. Tap the star on the ones that feel most exciting right now (your top ideas).', rows: 8, star: true, starHint: 'Get it all out! Big, small, wild or "someday" ideas — they all count. Tap the star on the ones that feel most exciting right now (your top ideas).', boxed: true },
      { type: 'labeled', id: 'explore', label: 'Idea explorer', hint: 'Choose ONE idea to explore a little more.', items: ['The idea', 'Why it excites me', 'What it could be', 'First tiny step'], boxed: true },
      { type: 'options', id: 'reality', label: 'Reality check', hint: 'A quick gut-check before you dive in.', options: ['Do I have the time or capacity right now?', 'Does it align with my bigger goals and values?', 'Does it feel energizing (not draining)?', 'Is now a good time, or should I park it for later?'], boxed: true },
      { type: 'options', id: 'park', single: true, other: true, label: 'Park it (for later)', hint: "Great ideas deserve a home, even if the timing isn't right yet.", options: ['Add to my "Someday List"', 'Revisit in 1 month', 'Revisit in 3 months', 'Revisit in 6 months'], boxed: true },
      nextStep("The one action I'll take (or schedule) for my idea is:"),
    ],
  },
  {
    id: 'idea-to-action',
    state: 'sparking',
    title: 'From Idea to Action',
    tagline: 'A simple plan to keep the momentum going',
    image: `${IMG}/idea-to-action.jpg`,
    when: 'When you have a great idea, feel motivated or inspired, and you want to turn that spark into something real (and not let it fizzle).',
    how: ['Capture your idea while it’s fresh.', 'Give it a little structure and a next step.', 'Add support and a timing plan to keep it moving forward.'],
    fields: [
      { type: 'lines', id: 'idea', label: 'The big idea', hint: 'What’s lighting me up right now?', rows: 3, boxed: true },
      { type: 'lines', id: 'intention', label: 'My intention', hint: 'What do I want this idea to create or bring into my life?', rows: 2, boxed: true },
      { type: 'checklist', id: 'steps', label: 'Break it down', hint: 'Turn the big idea into simple, doable steps.', labels: ['1', '2', '3', '4', '5'], boxed: true },
      { type: 'options', id: 'real', label: 'Keep it real', hint: 'A quick reality check to set it up for success.', boxed: true, options: ['Do I have the time and energy right now?', 'Is this aligned with my current goals and values?', 'Does it feel exciting and supportive (not stressful)?', 'What might get in the way, and how can I plan for it?'] },
      { type: 'options', id: 'timeline', single: true, inline: true, other: true, label: 'My timeline', hint: 'When do I want to take action?', options: ['Today', 'This week', 'This month', 'Within 3 months', 'Within 6 months'], boxed: true },
      { type: 'labeled', id: 'support', tick: true, label: 'My support plan', hint: 'What (or who) will help me follow through?', items: ['A person to share it with', 'A resource (book, course, tool)', 'A built-in reminder', 'Other support'], boxed: true },
      { type: 'quote', id: 'quote', lines: ['Capture the spark. Keep it simple. Take the next step.'] },
      nextStep("The one action I'll take in the next 24 hours is:"),
    ],
  },
];

export const sheets = rawSheets.map(applyLayout);

export const sheetsFor = (stateId) => sheets.filter((s) => s.state === stateId);
export const stateById = (id) => brainStates.find((s) => s.id === id);
